import React from 'react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { BaseSortableProps } from './@types/sortable-list-types';

import { SortableListContext } from './context/sortable-context';
import SortableCard from './components/Card/SortableCard';

export const Sortable = <T extends { id: number }>(
  props: BaseSortableProps<T>
) => {
  const {
    handleSortedData,
    accessor,
    listData,
    className,
    ListActions,
    ListContainer,
    placeholder,
  } = props;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const items = listData;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(
        (item) => (item['id'] as unknown as number) === active['id']
      );
      const newIndex = items.findIndex((item) => item.id === over?.id);
      const sortedData = arrayMove(items, oldIndex, newIndex);
      handleSortedData(sortedData);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
    >
      <SortableListContext.Provider
        value={{
          listData,
          handleSortedData,
          accessor,
          ListActions,
          ListContainer,
          placeholder,
        }}
      >
        <SortableContext
          items={listData.map((list) => list.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={`${className}`}>
            {React.Children.toArray(
              listData.map((list) => {
                return <SortableCard<typeof list> list={list} depth={0} />;
              })
            )}
          </div>
        </SortableContext>
      </SortableListContext.Provider>
    </DndContext>
  );
};
